import { store } from '@/app/atoms/store';
import { atom } from 'jotai';
import type { ConnectionType } from 'quadratic-shared/typesAndSchemasConnections';

/**
 * Mobile interaction modes:
 * - 'view': View-only mode. One-finger pan, two-finger zoom. No cell selection.
 * - 'cellSelected': Single cell is selected. Shows cell-specific actions.
 * - 'multiSelect': Multiple cells selected. Shows range-specific actions.
 */
export type MobileMode = 'view' | 'cellSelected' | 'multiSelect';

export const mobileModeAtom = atom<MobileMode>('view');

export const getMobileMode = () => store.get(mobileModeAtom);
export const setMobileMode = (mode: MobileMode) => store.set(mobileModeAtom, mode);

/**
 * Which mobile color picker is currently shown.
 * - 'none': No color picker is visible
 * - 'fill': Fill color picker is visible
 * - 'text': Text color picker is visible
 */
export type MobileColorPickerType = 'none' | 'fill' | 'text';
export const mobileColorPickerAtom = atom<MobileColorPickerType>('none');

export const getMobileColorPicker = () => store.get(mobileColorPickerAtom);
export const setMobileColorPicker = (type: MobileColorPickerType) => store.set(mobileColorPickerAtom, type);

/**
 * Whether the mobile border menu drawer is open.
 *
 * The drawer hosts the same border-action toggle group used on desktop, but
 * acquires the `useBorders()` results internally so the formatting bar can
 * trigger it without owning the borders hook.
 */
export const mobileBorderMenuAtom = atom<boolean>(false);

export const getMobileBorderMenu = () => store.get(mobileBorderMenuAtom);
export const setMobileBorderMenu = (open: boolean) => store.set(mobileBorderMenuAtom, open);

/**
 * Which mobile full-screen pane is currently visible.
 * Only one pane can be open at a time.
 * - 'none': No pane is visible
 * - 'ai': AI pane is visible
 * - 'settings': Spreadsheet settings pane is visible
 * - 'theme': Theme settings pane is visible
 * - 'conditionalFormat': Conditional formatting pane is visible
 * - 'dataValidation': Data validation pane is visible
 * - 'cellType': Cell type selection pane (Python, Formula, JS, etc.)
 * - 'codeEditor': Code editor pane for writing Python, Formula, JS, etc.
 * - 'data': Data menu root (Import, Connections, Google Sheets)
 * - 'dataAddConnection': Connection-type picker
 * - 'dataConnectionForm': Create/edit a connection
 * - 'dataGoogleSheets': Google Sheets import flow
 */
export type MobilePaneType =
  | 'none'
  | 'ai'
  | 'settings'
  | 'theme'
  | 'conditionalFormat'
  | 'dataValidation'
  | 'cellType'
  | 'codeEditor'
  | 'data'
  | 'dataAddConnection'
  | 'dataConnectionForm'
  | 'dataGoogleSheets';
export const mobilePaneAtom = atom<MobilePaneType>('none');

/**
 * Which sheet is currently being edited via the mobile sheet actions drawer.
 * - null: No drawer is open
 * - string: The sheet ID that the drawer is open for
 */
export const mobileSheetDrawerAtom = atom<string | null>(null);

export const getMobileSheetDrawer = () => store.get(mobileSheetDrawerAtom);
export const setMobileSheetDrawer = (sheetId: string | null) => store.set(mobileSheetDrawerAtom, sheetId);

/**
 * Whether the mobile file rename drawer is open.
 */
export const mobileFileRenameDrawerAtom = atom<boolean>(false);

export const getMobileFileRenameDrawer = () => store.get(mobileFileRenameDrawerAtom);
export const setMobileFileRenameDrawer = (open: boolean) => store.set(mobileFileRenameDrawerAtom, open);

/**
 * The connection currently being created or edited via the mobile data pane's
 * connection form screen (`mobilePane === 'dataConnectionForm'`).
 * - type: the connection type selected from the add-connection picker
 * - connectionUuid: set when editing an existing connection (future use)
 */
export type MobileDataConnectionFormState = {
  type: ConnectionType;
  connectionUuid?: string;
} | null;

export const mobileDataConnectionFormAtom = atom<MobileDataConnectionFormState>(null);

export const getMobileDataConnectionForm = () => store.get(mobileDataConnectionFormAtom);
export const setMobileDataConnectionForm = (state: MobileDataConnectionFormState) =>
  store.set(mobileDataConnectionFormAtom, state);
